// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// Relevant / Not relevant on a comment row (Figma November 2026, frame 0b,
// "Row not relevant reason"). Relevant is the existing Confirm (`liked: true`);
// Not relevant asks why, and a reason she saves becomes an enterprise rule.
import { Check, X } from "@phosphor-icons/react";
import {
  Button,
  Flex,
  IconButton,
  Popover,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type RejectionChoice,
  REJECTION_REASON_MAX_CHARS,
} from "./AskV2ReviewRejectScopePrompt";

export type AskV2ReviewCommentRowActionsProps = {
  commentId: string;
  liked?: boolean | null;
  /** Rejected here or in a past review; Not relevant then restores it. */
  rejected: boolean;
  disabled?: boolean;
  onJudge: (liked: boolean | null, rejection?: RejectionChoice) => void;
};

export function AskV2ReviewCommentRowActions({
  commentId,
  liked = null,
  rejected,
  disabled = false,
  onJudge,
}: AskV2ReviewCommentRowActionsProps) {
  const { t } = useTranslation("ui");
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  const relevant = liked === true;
  const reasonId = `ask-v2-review-row-reason-input-${commentId}`;
  const reasonLabel = t("askV2.review.rowReasonLabel");

  const reject = (choice: RejectionChoice) => {
    setAsking(false);
    setReason("");
    onJudge(false, choice);
  };

  return (
    <Flex
      gap="2"
      align="center"
      data-testid={`ask-v2-review-row-actions-${commentId}`}
    >
      <Tooltip content={t("askV2.review.rowRelevant")}>
        <IconButton
          type="button"
          size="1"
          variant={relevant ? "soft" : "ghost"}
          color={relevant ? "green" : "gray"}
          aria-label={t("askV2.review.rowRelevant")}
          aria-pressed={relevant}
          disabled={disabled}
          onClick={() => onJudge(relevant ? null : true)}
          data-testid={`ask-v2-review-row-relevant-${commentId}`}
        >
          <Check height="16" width="16" aria-hidden="true" />
        </IconButton>
      </Tooltip>
      <Popover.Root
        open={asking}
        onOpenChange={(open) => {
          // why: on a rejected comment the X is the way back, so it restores
          // at once instead of asking why it is not relevant a second time.
          if (open && rejected) {
            onJudge(null);
            return;
          }
          setAsking(open);
        }}
      >
        <Tooltip content={t("askV2.review.rowNotRelevant")}>
          <Popover.Trigger>
            <IconButton
              type="button"
              size="1"
              variant={rejected ? "soft" : "ghost"}
              color={rejected ? "red" : "gray"}
              aria-label={t("askV2.review.rowNotRelevant")}
              aria-pressed={rejected}
              disabled={disabled}
              data-testid={`ask-v2-review-row-not-relevant-${commentId}`}
            >
              <X height="16" width="16" aria-hidden="true" />
            </IconButton>
          </Popover.Trigger>
        </Tooltip>
        <Popover.Content
          align="start"
          className="max-h-[min(70vh,420px)] w-[300px] overflow-y-auto p-3"
          aria-label={reasonLabel}
          data-testid={`ask-v2-review-row-reason-${commentId}`}
        >
          {/* why: no label of its own; the popover around it already carries one. */}
          <form
            data-testid={`ask-v2-review-row-reason-form-${commentId}`}
            onSubmit={(event) => {
              event.preventDefault();
              if (trimmed) reject({ reach: "rule", reason: trimmed });
            }}
          >
            <label
              htmlFor={reasonId}
              className="block text-[12px] font-medium text-puntt-neutral-gray-11"
            >
              {reasonLabel}
            </label>
            <TextField.Root
              id={reasonId}
              data-testid={reasonId}
              size="2"
              mt="2"
              className="ph-no-capture"
              placeholder={t("askV2.review.rowReasonPlaceholder")}
              maxLength={REJECTION_REASON_MAX_CHARS}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <Flex gap="2" mt="3" justify="end">
              <Button
                type="button"
                size="1"
                variant="soft"
                color="gray"
                data-testid={`ask-v2-review-row-reason-skip-${commentId}`}
                onClick={() => reject({ reach: "once", reason: "" })}
              >
                {t("askV2.review.rowReasonSkip")}
              </Button>
              <Button
                type="submit"
                size="1"
                disabled={!trimmed}
                data-testid={`ask-v2-review-row-reason-save-${commentId}`}
              >
                {t("askV2.review.rowReasonSave")}
              </Button>
            </Flex>
          </form>
        </Popover.Content>
      </Popover.Root>
    </Flex>
  );
}
